import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Zap,
  CheckCircle,
  ArrowRight,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Sparkles,
  Clock,
  Calendar,
  Globe,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';

const benchmarkData = [
  { model: "gpt-4o-mini", t10: 8.4, t50: 51.7, t100: 84.1, t100c: 105.1, completion: 78, cost100: 0.0027 },
  { model: "gpt-4o", t10: 7.3, t50: 25.5, t100: 51.9, t100c: 43.9, completion: 100, cost100: 0.0573 },
  { model: "gpt-5.4", t10: 7.2, t50: 30.3, t100: 55.4, t100c: 56.0, completion: 100, cost100: 0.0446 },
  { model: "gpt-5.4-mini", t10: 3.3, t50: 13.4, t100: 17.2, t100c: 17.4, completion: 100, cost100: 0.0059 },
  { model: "gpt-5.4-nano", t10: 3.2, t50: 18.1, t100: 27.3, t100c: 22.9, completion: 100, cost100: 0.0023 },
  { model: "gpt-5-nano", t10: 24.3, t50: 112.9, t100: 140.2, t100c: 130.5, completion: 100, cost100: 0.0087 },
  { model: "gpt-5-mini", t10: 32.9, t50: 82.4, t100: 175.9, t100c: 132.1, completion: 100, cost100: 0.0132 },
];

// --- Hero Stats Section ---
const HeroStats = () => {
  const stats = [
    { label: "Faster", value: "5x", icon: Zap, color: "from-yellow-400 to-orange-500" },
    { label: "Sorted", value: "100%", icon: CheckCircle, color: "from-emerald-400 to-green-500" },
    { label: "Smarter", value: "GPT-5.4", icon: Sparkles, color: "from-blue-400 to-indigo-500" },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-16">
      {stats.map((stat, i) => {
        const Icon = stat.icon;
        return (
          <div
            key={i}
            className="relative group rounded-2xl p-[1px] bg-gradient-to-br opacity-0 animate-fadeIn"
            style={{ animationDelay: `${i * 150}ms`, animationFillMode: 'forwards' }}
          >
            <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${stat.color} opacity-20 group-hover:opacity-30 transition-opacity duration-300`} />
            <div className="relative bg-apple-700/60 backdrop-blur-md rounded-2xl p-6 text-center border border-apple-600/30">
              <Icon className="mx-auto mb-3 text-apple-200" size={28} />
              <div className="text-4xl sm:text-5xl font-bold text-apple-50 mb-1">{stat.value}</div>
              <div className="text-apple-300 text-sm font-medium uppercase tracking-wider">{stat.label}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// --- Animated Bar Chart ---
const SpeedBarChart = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const maxTime = 175.9;
  const models = benchmarkData
    .slice()
    .sort((a, b) => a.t100 - b.t100);

  const getBarColor = (model) => {
    if (model === "gpt-5.4-mini") return "#22c55e";
    if (model === "gpt-4o-mini") return "#ef4444";
    return "#6b7280";
  };

  return (
    <div className="bg-apple-700/40 backdrop-blur-md rounded-2xl border border-apple-600/30 p-6">
      <h3 className="text-lg font-semibold text-apple-50 mb-1">100-File Sort Speed</h3>
      <p className="text-apple-400 text-sm mb-6">Response time in seconds (lower is better)</p>
      <div className="space-y-3">
        {models.map((m) => {
          const pct = (m.t100 / maxTime) * 100;
          return (
            <div key={m.model} className="flex items-center gap-3">
              <div className="w-28 sm:w-36 text-right text-sm text-apple-300 font-mono shrink-0">
                {m.model}
              </div>
              <div className="flex-1 h-7 bg-apple-600/30 rounded-lg overflow-hidden relative">
                <div
                  className="h-full rounded-lg transition-all ease-out"
                  style={{
                    width: mounted ? `${pct}%` : '0%',
                    backgroundColor: getBarColor(m.model),
                    transitionDuration: '1.2s',
                  }}
                />
              </div>
              <div className="w-14 text-right text-sm font-mono text-apple-200 shrink-0">
                {m.t100}s
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// --- Before / After Cards ---
const BeforeAfterCards = () => {
  const before = { speed: "84.1s", completion: "78%" };
  const after = { speed: "17.2s", completion: "100%" };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Before */}
      <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span className="text-red-400 font-semibold text-sm uppercase tracking-wider">Before: GPT-4o-mini</span>
        </div>
        <div className="space-y-4">
          {[
            { label: "Speed (100 files)", value: before.speed },
            { label: "Completion Rate", value: before.completion },
          ].map((item, i) => (
            <div key={i} className="flex justify-between items-center">
              <span className="text-apple-300 text-sm">{item.label}</span>
              <span className="text-red-400 font-mono font-bold text-lg">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* After */}
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-3 h-3 rounded-full bg-emerald-500" />
          <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">After: GPT-5.4-mini</span>
        </div>
        <div className="space-y-4">
          {[
            { label: "Speed (100 files)", value: after.speed },
            { label: "Completion Rate", value: after.completion },
          ].map((item, i) => (
            <div key={i} className="flex justify-between items-center">
              <span className="text-apple-300 text-sm">{item.label}</span>
              <span className="text-emerald-400 font-mono font-bold text-lg">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// --- File Completion Grid ---
const FileCompletionGrid = () => {
  const oldCompleted = 78;
  const newCompleted = 100;

  const renderGrid = (completed, label, sublabel, borderColor) => (
    <div className={`rounded-2xl border p-5 ${borderColor}`}>
      <div className="grid grid-cols-10 gap-1 mb-3">
        {Array.from({ length: 100 }, (_, i) => (
          <div
            key={i}
            className={`w-full aspect-square rounded-sm ${
              i < completed ? 'bg-emerald-500' : 'bg-red-500'
            }`}
          />
        ))}
      </div>
      <div className="text-center">
        <span className="text-apple-50 font-semibold text-sm">{label}</span>
        <span className="text-apple-400 text-xs block">{sublabel}</span>
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {renderGrid(oldCompleted, "GPT-4o-mini", "22 files lost", "border-red-500/30 bg-red-500/5")}
      {renderGrid(newCompleted, "GPT-5.4-mini", "100% sorted", "border-emerald-500/30 bg-emerald-500/5")}
    </div>
  );
};

// --- Interactive Benchmark Table ---
const BenchmarkTable = () => {
  const [sortKey, setSortKey] = useState('t100');
  const [sortAsc, setSortAsc] = useState(true);

  const columns = [
    { key: 'model', label: 'Model' },
    { key: 't10', label: '10 files' },
    { key: 't50', label: '50 files' },
    { key: 't100', label: '100 files' },
    { key: 't100c', label: '100+content' },
    { key: 'completion', label: 'Completion' },
  ];

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const sorted = benchmarkData.slice().sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (typeof aVal === 'string') {
      return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortAsc ? aVal - bVal : bVal - aVal;
  });

  const formatCell = (key, value) => {
    if (key === 'model') return value;
    if (key === 'completion') return `${value}%`;
    return `${value}s`;
  };

  return (
    <div className="bg-apple-700/40 backdrop-blur-md rounded-2xl border border-apple-600/30 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-apple-600/30">
              {columns.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className="px-4 py-3 text-left text-apple-300 font-medium cursor-pointer hover:text-apple-50 transition-colors select-none whitespace-nowrap"
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {sortKey === col.key ? (
                      sortAsc ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                    ) : (
                      <ArrowUpDown size={12} className="opacity-30" />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const isHighlight = row.model === "gpt-5.4-mini";
              const isOld = row.model === "gpt-4o-mini";
              return (
                <tr
                  key={row.model}
                  className={`border-b border-apple-600/20 transition-colors ${
                    isHighlight
                      ? 'bg-emerald-500/10 hover:bg-emerald-500/15'
                      : isOld
                      ? 'bg-red-500/5 hover:bg-red-500/10'
                      : 'hover:bg-apple-600/20'
                  }`}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`px-4 py-3 font-mono whitespace-nowrap ${
                        col.key === 'model'
                          ? isHighlight
                            ? 'text-emerald-400 font-semibold'
                            : isOld
                            ? 'text-red-400'
                            : 'text-apple-200'
                          : 'text-apple-300'
                      }`}
                    >
                      {formatCell(col.key, row[col.key])}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-xs text-apple-400 border-t border-apple-600/20">
        Click any column header to sort
      </div>
    </div>
  );
};

// --- Multilingual Examples ---
const MultilingualExamples = () => {
  const examples = [
    { flag: "\ud83c\udde9\ud83c\uddea", lang: "German", prompt: "Sortiere nach Autoren", result: "Organized by author name" },
    { flag: "\ud83c\uddeb\ud83c\uddf7", lang: "French", prompt: "Classe les fichiers par cat\u00e9gorie", result: "Sorted into French-language categories" },
    { flag: "\ud83c\uddf9\ud83c\uddf7", lang: "Turkish", prompt: "Klas\u00f6rleri t\u00fcrk\u00e7e kategorize et", result: "Turkish folder names" },
  ];

  return (
    <div className="space-y-4">
      {examples.map((ex, i) => (
        <div key={i} className="bg-apple-700/40 backdrop-blur-md rounded-xl border border-apple-600/30 p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">{ex.flag}</span>
            <span className="text-apple-300 text-xs font-medium uppercase tracking-wider">{ex.lang}</span>
          </div>
          <div className="bg-apple-800/80 rounded-lg p-3 font-mono text-sm text-apple-100 mb-2">
            &gt; "{ex.prompt}"
          </div>
          <p className="text-apple-400 text-sm">{ex.result}</p>
        </div>
      ))}
    </div>
  );
};

// --- Cost Calculator ---
// --- Time Calculator ---
const TimeCalculator = () => {
  const [fileCount, setFileCount] = useState(100);
  const oldTimePer100 = 84.1;
  const newTimePer100 = 17.2;

  const oldTime = ((fileCount / 100) * oldTimePer100).toFixed(1);
  const newTime = ((fileCount / 100) * newTimePer100).toFixed(1);
  const timeSaved = (oldTime - newTime).toFixed(1);

  return (
    <div className="bg-apple-700/40 backdrop-blur-md rounded-2xl border border-apple-600/30 p-6">
      <h3 className="text-lg font-semibold text-apple-50 mb-4">Speed Calculator</h3>
      <div className="mb-6">
        <label className="block text-apple-300 text-sm mb-2">Number of files</label>
        <input
          type="range"
          min={10}
          max={500}
          step={10}
          value={fileCount}
          onChange={(e) => setFileCount(Number(e.target.value))}
          className="w-full accent-sortio-primary"
        />
        <div className="text-center text-apple-50 font-mono text-2xl font-bold mt-2">{fileCount} files</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4">
          <div className="text-red-400 text-xs font-semibold uppercase tracking-wider mb-3">Before (GPT-4o-mini)</div>
          <div className="flex justify-between text-sm">
            <span className="text-apple-400">Estimated time</span>
            <span className="text-red-400 font-mono">{oldTime}s</span>
          </div>
        </div>
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
          <div className="text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-3">After (GPT-5.4-mini)</div>
          <div className="flex justify-between text-sm">
            <span className="text-apple-400">Estimated time</span>
            <span className="text-emerald-400 font-mono">{newTime}s</span>
          </div>
        </div>
      </div>
      {timeSaved > 0 && (
        <div className="mt-4 text-center text-apple-300 text-sm">
          You save <span className="text-emerald-400 font-semibold">{timeSaved}s</span> per sort with the new model
        </div>
      )}
    </div>
  );
};

// --- Main Page ---
const BenchmarkBlogPage = () => {
  const breadcrumbItems = [
    { label: 'Blog', href: null },
    { label: 'AI Model Upgrade', href: null },
  ];

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Sortio's AI Just Got 5x Faster and Smarter",
    "description": "We upgraded Sortio's AI engine from GPT-4o-mini to GPT-5.4-mini. Real benchmarks show 5x faster sorting, 100% file completion, and better folder structures.",
    "url": "https://www.getsortio.com/articles/ai-model-upgrade",
    "datePublished": "2026-03-30",
    "dateModified": "2026-03-30",
    "author": {
      "@type": "Organization",
      "name": "Sortio",
      "url": "https://www.getsortio.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Sortio",
      "url": "https://www.getsortio.com"
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#121212" }}>
      <SEOHead
        title="Sortio's AI Just Got 5x Faster | AI Model Upgrade Benchmarks"
        description="We upgraded Sortio's AI engine from GPT-4o-mini to GPT-5.4-mini. Real benchmarks show 5x faster sorting, 100% file completion, and better folder structures."
        canonical="https://www.getsortio.com/articles/ai-model-upgrade"
        schema={blogSchema}
        keywords="Sortio AI upgrade, GPT-5.4-mini benchmark, file sorting speed, AI file organizer performance"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Article Header */}
        <header className="mb-12">
          <div className="inline-block mb-4">
            <div className="flex items-center p-1.5 rounded-full bg-sortio-secondary/10 px-4 py-1.5">
              <Sparkles className="mr-2 text-sortio-secondary" size={14} />
              <span className="text-xs font-medium uppercase tracking-wider text-sortio-secondary">Engineering Update</span>
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold mb-6 tracking-tight text-apple-50 leading-tight">
            Sortio's AI Just Got{' '}
            <span className="apple-gradient-text">5x Faster</span> and Smarter
          </h1>
          <p className="text-apple-300 text-lg sm:text-xl max-w-3xl font-light leading-relaxed mb-4">
            We shipped a major upgrade to Sortio's AI engine. Every sort you run is now powered by GPT-5.4-mini instead of GPT-4o-mini. Here's what that means, with real benchmarks.
          </p>
          <div className="flex items-center gap-4 text-apple-400 text-sm">
            <span>By the Sortio team</span>
            <span className="flex items-center gap-1"><Calendar size={14} /> March 30, 2026</span>
            <span className="flex items-center gap-1"><Clock size={14} /> 5 min read</span>
          </div>
        </header>

        {/* Hero Stats */}
        <HeroStats />

        {/* Speed Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-semibold text-apple-50 mb-2">The Numbers</h2>
          <p className="text-apple-300 mb-8 leading-relaxed">
            We benchmarked 7 models across 6 test scenarios: 10, 50, and 100 files with and without content analysis. Here are the results that matter.
          </p>

          <h3 className="text-xl font-semibold text-apple-100 mb-4 flex items-center gap-2">
            <Zap size={20} className="text-yellow-400" /> Speed
          </h3>
          <SpeedBarChart />

          <p className="text-apple-300 mt-4 text-sm leading-relaxed">
            That's not a typo. Sorting 100 files now takes 17 seconds instead of a minute and a half.
          </p>
        </section>

        {/* Before / After */}
        <section className="mb-16">
          <h3 className="text-xl font-semibold text-apple-100 mb-6">Before vs. After</h3>
          <BeforeAfterCards />
        </section>

        {/* Accuracy Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-semibold text-apple-50 mb-2">Accuracy</h2>
          <p className="text-apple-300 mb-6 leading-relaxed">
            GPT-4o-mini had a critical flaw: at 100 files, it would <strong className="text-apple-100">drop files</strong>. It ran out of output capacity before it could sort everything. With GPT-5.4-mini, every file gets sorted, every time.
          </p>
          <FileCompletionGrid />
        </section>

        {/* Real-World Examples */}
        <section className="mb-16">
          <h2 className="text-3xl font-semibold text-apple-50 mb-2">What This Looks Like in Practice</h2>
          <p className="text-apple-300 mb-8 leading-relaxed">
            Here are example prompts and what the new engine produces:
          </p>

          <div className="space-y-6">
            {[
              {
                title: "Music Production Workflow",
                files: "200+ files",
                prompt: "Separate audio by stems (WAV, mp3, aiff), tracks (WAV, mp3), Ableton Projects. Documents in folders depending on use. Misc in a single folder, organized by type and date.",
                result: "Audio/Stems, Audio/Tracks, Ableton Projects, Documents/Word-PDF, Media/Audiobooks: domain-specific folders with date-based sub-organization.",
              },
              {
                title: "Research Paper Library",
                files: "400+ files",
                prompt: "Scientific literature in 'Papers' divided by research area. Protocols in their own folder. Data analysis scripts organized by project.",
                result: "Papers/Field Studies, Papers/Urban Ecology, Protocols, Scripts/Analysis, Data/Raw: academic file organization a researcher would actually use.",
              },
              {
                title: "Applications Folder",
                files: "189 files",
                prompt: "Sort by type like utilities, gaming, system etc",
                result: "AI/Chatbots, Audio/Editors, Browsers, Dev, Gaming/Launchers, System/Diagnostics, Utilities/Password Managers: intelligent categorization beyond file extensions.",
              },
            ].map((ex, i) => (
              <div key={i} className="bg-apple-700/40 backdrop-blur-md rounded-2xl border border-apple-600/30 p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-apple-50">{ex.title}</h3>
                  <span className="text-apple-400 text-xs font-mono">{ex.files}</span>
                </div>
                <div className="bg-apple-800/80 rounded-lg p-4 font-mono text-sm text-apple-100 mb-3 leading-relaxed">
                  &gt; "{ex.prompt}"
                </div>
                <p className="text-apple-300 text-sm leading-relaxed"><strong className="text-apple-200">Result:</strong> {ex.result}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Non-English Prompts */}
        <section className="mb-16">
          <h3 className="text-xl font-semibold text-apple-100 mb-2 flex items-center gap-2">
            <Globe size={20} className="text-sortio-primary" /> Works in Any Language
          </h3>
          <p className="text-apple-300 mb-6 text-sm leading-relaxed">
            Write your prompt in German, French, Spanish, Turkish: the AI understands and creates folder names in your language.
          </p>
          <MultilingualExamples />
        </section>

        {/* Why We Switched */}
        <section className="mb-16">
          <h2 className="text-3xl font-semibold text-apple-50 mb-4">Why We Switched</h2>
          <div className="text-apple-300 space-y-4 leading-relaxed">
            <p>
              GPT-4o-mini was Sortio's engine since launch. It was cheap and fast enough, but "fast enough" meant we had to work around a 30-second server timeout by splitting large folders into tiny batches of 10 files, sorting each batch separately, then stitching the results together on your machine.
            </p>
            <p>
              The problem: each batch only sees 10 files. The AI can't make global decisions about folder structure. It might create "Documents" in batch 1, then "Docs" in batch 3, then "Files" in batch 5, all for the same type of content.
            </p>
            <p>
              We also moved our entire infrastructure to Google Cloud, removing a 30-second server timeout that previously forced the batching approach. With that constraint gone and GPT-5.4-mini's capabilities, we now send all your files in a <strong className="text-apple-100">single request</strong>. The AI sees everything at once and creates one coherent folder structure. No stitching, no duplicates, no missed files.
            </p>
          </div>
        </section>

        {/* What This Means */}
        <section className="mb-16">
          <h2 className="text-3xl font-semibold text-apple-50 mb-6">What This Means for You</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: Zap, text: "Sorts complete in seconds, not minutes. 100 files in 17 seconds." },
              { icon: CheckCircle, text: "No more missing files. 100% completion rate at every file count." },
              { icon: Sparkles, text: "Better folder structures. The AI sees all your files at once." },
              { icon: Globe, text: "Works in any language. German, French, Turkish, Spanish." },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex items-start gap-3 bg-apple-700/40 backdrop-blur-md rounded-xl border border-apple-600/30 p-4">
                  <Icon size={20} className="text-sortio-primary shrink-0 mt-0.5" />
                  <p className="text-apple-300 text-sm leading-relaxed">{item.text}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Cost Calculator */}
        <section className="mb-16">
          <h2 className="text-3xl font-semibold text-apple-50 mb-6">Estimate Your Sort</h2>
          <TimeCalculator />
        </section>

        {/* Full Benchmark Table */}
        <section className="mb-16">
          <h2 className="text-3xl font-semibold text-apple-50 mb-2">Full Benchmark Data</h2>
          <p className="text-apple-300 mb-6 text-sm leading-relaxed">
            For the technically curious: every model we tested. Click any column to sort.
          </p>
          <BenchmarkTable />
        </section>

        {/* Upgrade Note */}
        <section className="mb-16">
          <div className="bg-sortio-primary/10 rounded-2xl border border-sortio-primary/20 p-6 text-center">
            <p className="text-apple-200 leading-relaxed">
              The upgrade is live now. <strong className="text-apple-50">No app update needed</strong>: the improvement is server-side. Open Sortio and try it.
            </p>
          </div>
        </section>

        {/* Keep reading: bring this page into the /blog/ internal-link graph */}
        <section className="mb-16 pt-8 border-t border-apple-700/30">
          <h2 className="text-xl font-semibold text-apple-50 mb-4">Keep reading</h2>
          <div className="grid gap-3">
            <Link
              to="/blog/automatically-rename-pdfs-by-content"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Automatically rename PDFs by content
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/ai-sort-vs-rule-builder-which-to-use"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                AI Sort vs Rule Builder: which to use when
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
            <Link
              to="/blog/local-ai-vs-cloud-ai-file-organization"
              className="apple-panel p-4 rounded-apple group hover:ring-1 hover:ring-sortio-primary/30 transition-all flex items-center justify-between"
            >
              <span className="text-apple-100 group-hover:text-sortio-primary transition-colors">
                Local AI vs cloud AI for file organization
              </span>
              <ArrowRight className="w-4 h-4 text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all" />
            </Link>
          </div>
        </section>

        {/* CTA */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-3xl p-8 border border-sortio-primary/20">
            <Sparkles className="mx-auto mb-4 text-sortio-primary" size={32} />
            <h3 className="text-2xl font-semibold text-apple-50 mb-4">
              Experience the Upgrade
            </h3>
            <p className="text-apple-300 mb-6 max-w-2xl mx-auto">
              5x faster sorting, 100% completion, better folder structures. Try it now.
            </p>
            <Link
              to="/download"
              className="inline-flex items-center px-8 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
            >
              Try Sortio Free
              <ArrowRight className="ml-2" size={16} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BenchmarkBlogPage;
